/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ElementType } from 'react';

/**
 * Shared "Typography" and "Font Family" Page Designer attribute vocabulary.
 *
 * First introduced on `Content.hero` (Title/Subtitle Typography + Font Family), reused by any
 * other component exposing the same standardized controls (e.g. `Layout.productCarousel`,
 * `Layout.popularCategories`) so authors get one consistent vocabulary across components instead
 * of a different enum per component.
 */
export const TYPOGRAPHY_VALUES = [
    'Default',
    'Paragraph',
    'Heading 1',
    'Heading 2',
    'Heading 3',
    'Heading 4',
    'Heading 5',
    'Heading 6',
] as const;

export type Typography = (typeof TYPOGRAPHY_VALUES)[number];

/** Heading levels map to their semantic tag for SEO structure; Default/Paragraph fall back to the caller's default tag. */
export const HEADING_TAG: Partial<Record<Typography, ElementType>> = {
    'Heading 1': 'h1',
    'Heading 2': 'h2',
    'Heading 3': 'h3',
    'Heading 4': 'h4',
    'Heading 5': 'h5',
    'Heading 6': 'h6',
};

export function normalizeTypography(value: string | undefined): Typography {
    if (value && (TYPOGRAPHY_VALUES as readonly string[]).includes(value)) {
        return value as Typography;
    }
    return 'Default';
}

// The first 7 are the "top-level" families — one entry per distinct face/weight. The remaining 8
// are common combinations (sans italics, plus the accent/script synthetic weights added to
// fonts.css) layered on top. Values are additive-only: existing values must never be renamed or
// removed, since they're stored as free-text strings in already-authored Page Designer content.
export const FONT_FAMILY_VALUES = [
    // Top 7
    'Default',
    'Sans Light',
    'Sans Book',
    'Sans Medium',
    'Sans Bold',
    'Accent',
    'Script',
    // 8 common combinations
    'Sans Medium Italic',
    'Sans Bold Italic',
    'Accent Light',
    'Accent Semibold',
    'Accent Bold',
    'Script Light',
    'Script Semibold',
    'Script Bold',
] as const;
export type FontFamily = (typeof FONT_FAMILY_VALUES)[number];

export const FONT_FAMILY_CLASS: Record<FontFamily, string> = {
    Default: '',
    'Sans Light': 'font-sans-light',
    'Sans Book': 'font-sans-book',
    'Sans Medium': 'font-sans-medium',
    'Sans Bold': 'font-sans-bold',
    Accent: 'font-accent',
    Script: 'font-script',
    'Sans Medium Italic': 'font-sans-medium-italic',
    'Sans Bold Italic': 'font-sans-bold-italic',
    'Accent Light': 'font-stjude-accent-light',
    'Accent Semibold': 'font-stjude-accent-semibold',
    'Accent Bold': 'font-stjude-accent-bold',
    'Script Light': 'font-stjude-script-light',
    'Script Semibold': 'font-stjude-script-semibold',
    'Script Bold': 'font-stjude-script-bold',
};

export function normalizeFontFamily(value: string | undefined): FontFamily {
    if (value && (FONT_FAMILY_VALUES as readonly string[]).includes(value)) {
        return value as FontFamily;
    }
    return 'Default';
}
